/**
 * Per-document record of "which server version is this tab based on".
 *
 * Stale-tab data-loss guard (issue #2347). Two independent pieces of the editor
 * need to agree on this baseline:
 *
 *  - `useConcurrentEditPolling`, which raises the concurrent-edit banner, and
 *  - the providers' `silentSave` autosave, which must refuse to PUT when the
 *    server has moved past the baseline.
 *
 * They live in different component trees (the translation pane is a separate
 * provider for a different document), so the baseline is held in this module
 * rather than in React state. Module scope is exactly the right lifetime here:
 * one record per document per browser tab, which is the unit the hazard is
 * defined over — a *tab* holding a stale document.
 *
 * The baseline only ever advances via this tab's own successful saves or an
 * explicit reload from the server. It is deliberately never advanced by merely
 * observing that the server changed: that observation is the conflict.
 */

import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL } from '@utils/api/endpoints.ts';

/** Identifies one document within one tab. */
export function syncKey(
  sessionId: string | undefined,
  documentId: string | undefined
): string {
  return `${sessionId ?? ''}:${documentId ?? ''}`;
}

/**
 * Parse a server `last_modified` into epoch ms, returning 0 for anything
 * unusable. Callers treat 0 as "unknown" and refuse to autosave on it.
 */
export function parseLastModified(value: unknown): number {
  if (value == null) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value !== 'string') return 0;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

const baselines = new Map<string, number>();
const remoteEditListeners = new Map<string, Set<() => void>>();

/** Server `last_modified` (epoch ms) this tab's document is based on; 0 = unknown. */
export function getBaseline(key: string): number {
  return baselines.get(key) ?? 0;
}

/**
 * Record the server version this tab is now based on. Accepts a raw
 * `last_modified` string so callers can pass API payloads straight through.
 *
 * Monotonic by design: an older value never replaces a newer one, so an
 * out-of-order response cannot walk the baseline backwards and manufacture a
 * phantom conflict.
 */
export function setBaseline(key: string, lastModified: unknown): void {
  const ms = parseLastModified(lastModified);
  if (ms <= 0) return;
  const current = baselines.get(key) ?? 0;
  if (ms > current) baselines.set(key, ms);
}

/** Forget a document's baseline (editor unmount / cleanup). */
export function clearBaseline(key: string): void {
  baselines.delete(key);
  remoteEditListeners.delete(key);
}

/**
 * Subscribe to "a concurrent edit was detected for this document".
 *
 * Lets an autosave that resolved to `conflict` raise the same banner the
 * poller raises, so there is one notion of the conflicted state regardless of
 * which code path noticed it first (the poller is skipped in hidden tabs, so
 * autosave is frequently the first to know).
 */
export function subscribeRemoteEdit(key: string, fn: () => void): () => void {
  const set = remoteEditListeners.get(key) ?? new Set<() => void>();
  set.add(fn);
  remoteEditListeners.set(key, set);
  return () => {
    set.delete(fn);
  };
}

/** Announce a detected concurrent edit to every listener for this document. */
export function notifyRemoteEdit(key: string): void {
  const set = remoteEditListeners.get(key);
  if (!set) return;
  for (const fn of [...set]) fn();
}

/**
 * Read the server's current `last_modified` for a private document without
 * downloading the body.
 *
 * Prefers the lightweight `edit-meta` endpoint and falls back to the document
 * endpoint with `direct_download` (which returns metadata plus a blob URL we
 * simply do not fetch). Returns `null` when neither answers — the caller must
 * then treat the server state as unverified rather than assume it is unchanged.
 */
export async function probeServerLastModified(
  sessionId: string,
  documentId: string
): Promise<number | null> {
  if (!sessionId || !documentId) return null;
  try {
    const { data } = await Axios.get<{
      result: { last_modified: string };
    }>(GET_URL(sessionId, documentId).documentEditMeta);
    const ms = parseLastModified(data?.result?.last_modified);
    return ms > 0 ? ms : null;
  } catch {
    try {
      const { data } = await Axios.get<{
        result: { last_modified: string };
      }>(GET_URL(sessionId, documentId).document, {
        params: { direct_download: true }
      });
      const ms = parseLastModified(data?.result?.last_modified);
      return ms > 0 ? ms : null;
    } catch {
      return null;
    }
  }
}

/**
 * Shared-link variant of {@link probeServerLastModified}. Shared documents are
 * token-authenticated and have no `edit-meta` endpoint, so this reads metadata
 * from the shared document endpoint and ignores the body.
 */
export async function probeSharedServerLastModified(
  sessionId: string,
  documentId: string,
  token: string | undefined
): Promise<number | null> {
  if (!sessionId || !documentId || !token) return null;
  try {
    const { data } = await Axios.get<{
      result: { last_modified: string };
    }>(GET_URL(sessionId, documentId).sharedDocument, {
      params: { token, direct_download: true }
    });
    const ms = parseLastModified(data?.result?.last_modified);
    return ms > 0 ? ms : null;
  } catch {
    return null;
  }
}

/**
 * Pull the new `last_modified` out of an update (PUT) response when the backend
 * includes it, so a successful save can advance the baseline without a second
 * round trip. Returns 0 when the response carries no usable timestamp.
 *
 * Both response shapes in use are accepted: the private document mutation
 * resolves to the raw Axios response (`{ data: { result } }`) while the shared
 * one already unwraps it (`{ result }`).
 */
export function lastModifiedFromUpdateResponse(response: unknown): number {
  const envelope = response as
    | {
        data?: { result?: { last_modified?: unknown } };
        result?: { last_modified?: unknown };
      }
    | undefined;
  return (
    parseLastModified(envelope?.data?.result?.last_modified) ||
    parseLastModified(envelope?.result?.last_modified)
  );
}
